// Streaming fragmented-MP4 muxer around mediabunny.
//
// Uses the Mp4OutputFormat box callbacks for framing (verified: ftyp, moov,
// then repeating moof+mdat pairs, all in order): ftyp+moov become the init
// segment; each moof+mdat pair becomes one media segment. The relay pipes
// these bytes into `ffmpeg -f mp4 -i pipe:0 -c:v copy -c:a aac`.

import {
  Output,
  Mp4OutputFormat,
  NullTarget,
  EncodedVideoPacketSource,
  EncodedAudioPacketSource,
  EncodedPacket,
} from "mediabunny";

const MIN_FRAGMENT_S = 0.5;

export function createStreamingMuxer({ fps, onInit, onSegment, onError }) {
  let ftyp = null;
  let pendingMoof = null;

  const output = new Output({
    target: new NullTarget(),
    format: new Mp4OutputFormat({
      fastStart: "fragmented",
      minimumFragmentDuration: MIN_FRAGMENT_S,
      onFtyp: (data) => {
        ftyp = data.slice();
      },
      onMoov: (data) => {
        const init = new Uint8Array(ftyp.length + data.length);
        init.set(ftyp);
        init.set(data, ftyp.length);
        onInit(init);
      },
      onMoof: (data) => {
        pendingMoof = data.slice();
      },
      onMdat: (data) => {
        if (!pendingMoof) return;
        const seg = new Uint8Array(pendingMoof.length + data.length);
        seg.set(pendingMoof);
        seg.set(data, pendingMoof.length);
        pendingMoof = null;
        onSegment(seg);
      },
    }),
  });

  const videoSource = new EncodedVideoPacketSource("avc");
  const audioSource = new EncodedAudioPacketSource("opus");
  output.addVideoTrack(videoSource, { frameRate: fps });
  output.addAudioTrack(audioSource);

  const started = output.start();
  let videoMetaSent = false;
  let audioMetaSent = false;
  // add() must be awaited in order per track; serialize with promise chains.
  let chain = started;
  const enqueue = (fn) => {
    chain = chain.then(fn).catch((e) => onError?.(e));
  };

  return {
    addVideoChunk(chunk, meta) {
      enqueue(() => {
        const pkt = EncodedPacket.fromEncodedChunk(chunk);
        const first = !videoMetaSent;
        videoMetaSent = true;
        return videoSource.add(pkt, first ? { decoderConfig: meta.decoderConfig } : undefined);
      });
    },
    addAudioChunk(chunk, meta) {
      enqueue(() => {
        const pkt = EncodedPacket.fromEncodedChunk(chunk);
        const first = !audioMetaSent;
        audioMetaSent = true;
        return audioSource.add(pkt, first ? { decoderConfig: meta.decoderConfig } : undefined);
      });
    },
    async finalize() {
      await chain.catch(() => {});
      await output.finalize().catch(() => {});
    },
  };
}
